import * as THREE from 'three';

export class Renderer {
  readonly canvas: HTMLCanvasElement;
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  private sun: THREE.DirectionalLight;
  private pixelRatioCap = 2;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
      alpha: false,
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0c0d14);
    this.scene.fog = new THREE.Fog(0x0c0d14, 60, 220);

    this.camera = new THREE.PerspectiveCamera(72, 1, 0.1, 500);
    this.camera.position.set(0, 5, 8);
    this.camera.lookAt(0, 1, 0);

    // Lighting: hemi (cool sky + warm ground) + directional sun.
    const hemi = new THREE.HemisphereLight(0x6d7fff, 0x231a3a, 0.6);
    this.scene.add(hemi);

    this.sun = new THREE.DirectionalLight(0xfff3d6, 1.5);
    this.sun.position.set(40, 80, 30);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.camera.near = 1;
    this.sun.shadow.camera.far = 220;
    this.sun.shadow.camera.left = -80;
    this.sun.shadow.camera.right = 80;
    this.sun.shadow.camera.top = 80;
    this.sun.shadow.camera.bottom = -80;
    this.sun.shadow.bias = -0.0005;
    this.scene.add(this.sun);
  }

  setLodTier(tier: 'low' | 'med' | 'high' | 'ultra'): void {
    switch (tier) {
      case 'low':
        this.renderer.shadowMap.enabled = false;
        this.pixelRatioCap = 1;
        break;
      case 'med':
        this.renderer.shadowMap.enabled = true;
        this.sun.shadow.mapSize.set(1024, 1024);
        this.pixelRatioCap = 1.25;
        break;
      case 'high':
        this.renderer.shadowMap.enabled = true;
        this.sun.shadow.mapSize.set(2048, 2048);
        this.pixelRatioCap = 1.5;
        break;
      case 'ultra':
        this.renderer.shadowMap.enabled = true;
        this.sun.shadow.mapSize.set(2048, 2048);
        this.pixelRatioCap = 2;
        break;
    }
    this.resize();
  }

  resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio, this.pixelRatioCap);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }
}
